import { CONSTANTS } from '../data';

let audioContext: AudioContext | null = null;

function initAudioContext() {
  if (!audioContext) {
    try {
        audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch(e) {
        console.error("Web Audio API is not supported in this browser");
        return null;
    }
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  return audioContext;
}

const audioBuffers: { [key: string]: AudioBuffer } = {};
const managedAudio: { [key: string]: { source: AudioBufferSourceNode, gainNode: GainNode } } = {};
let activeSources = new Set<AudioScheduledSourceNode>();

async function loadAudio(key: string, path: string) {
    const ctx = initAudioContext();
    if (!ctx || audioBuffers[key]) return;

    try {
        const response = await fetch(path);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
        audioBuffers[key] = audioBuffer;
    } catch (e) {
        console.error(`Error loading audio ${key} from ${path}:`, e);
    }
}

function playLoopingSound(key: string) {
    const ctx = initAudioContext();
    if (!ctx || !audioBuffers[key] || managedAudio[key]?.source) return;

    const source = ctx.createBufferSource();
    source.buffer = audioBuffers[key];
    source.loop = true;

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(CONSTANTS.SOUNDS.volume, ctx.currentTime);

    source.connect(gainNode);
    gainNode.connect(ctx.destination);
    source.start();

    managedAudio[key] = { source, gainNode };
    activeSources.add(source);
}

function stopLoopingSound(key: string, fadeOutDuration: number = 0.5) {
    const ctx = initAudioContext();
    const audio = managedAudio[key];
    if (!ctx || !audio || !audio.source || !audio.gainNode) return;

    audio.gainNode.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + fadeOutDuration);
    
    try {
        audio.source.stop(ctx.currentTime + fadeOutDuration);
    } catch(e) {
        // Ignore if it's already stopping.
    }
    
    audio.source.onended = () => {
        activeSources.delete(audio.source);
        if (managedAudio[key]) {
            managedAudio[key].source?.disconnect();
            managedAudio[key].gainNode?.disconnect();
            delete managedAudio[key];
        }
    };
}

function playTone(frequency: number, duration: number, type: OscillatorType = 'sine') {
  try {
    const ctx = initAudioContext();
    if (!ctx) return;
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.frequency.value = frequency;
    oscillator.type = type;
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    gainNode.gain.setValueAtTime(CONSTANTS.SOUNDS.volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(CONSTANTS.SOUNDS.fadeOutTime, ctx.currentTime + duration);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);

    activeSources.add(oscillator);
    oscillator.onended = () => {
        try {
            gainNode.disconnect();
            oscillator.disconnect();
            activeSources.delete(oscillator);
        } catch (e) {
            // may already be disconnected
        }
    };
  } catch (error) {
    console.warn('Audio playback failed:', error);
  }
}

function playArpeggio(frequencies: number[], noteDuration: number) {
  try {
    const ctx = initAudioContext();
    if (!ctx) return;
    
    frequencies.forEach((frequency, index) => {
      const startTime = ctx.currentTime + (index * noteDuration * 0.8);
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(CONSTANTS.SOUNDS.volume, startTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(CONSTANTS.SOUNDS.fadeOutTime, startTime + noteDuration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + noteDuration);

      activeSources.add(oscillator);
      oscillator.onended = () => {
        try {
            gainNode.disconnect();
            oscillator.disconnect();
            activeSources.delete(oscillator);
        } catch(e) {
            // may already be disconnected
        }
      };
    });
  } catch (error) {
    console.warn('Audio playback failed:', error);
  }
}

function stopAllSounds() {
    const ctx = initAudioContext();
    if (!ctx) return;

    // Stop urgency sounds
    urgencySounds.stop();

    // Stop all other sounds
    const sourcesToStop = new Set(activeSources);
    sourcesToStop.forEach(source => {
        try {
            source.stop(ctx.currentTime);
            source.disconnect();
        } catch (e) {
            // may already be stopped/disconnected
        }
    });
    activeSources.clear();

    // Clear the managedAudio registry for looping sounds
    Object.keys(managedAudio).forEach(key => {
        delete managedAudio[key];
    });
}

export const sounds: Record<string, Function> = {
  menuHover: () => playTone(CONSTANTS.SOUNDS.frequencies.menuHover, CONSTANTS.SOUNDS.durations.menuHover),
  buttonClick: () => playTone(CONSTANTS.SOUNDS.frequencies.buttonClick, CONSTANTS.SOUNDS.durations.buttonClick),
  combinationFail: () => playTone(CONSTANTS.SOUNDS.frequencies.combinationFail, CONSTANTS.SOUNDS.durations.combinationFail),
  aiRefusal: () => playTone(CONSTANTS.SOUNDS.frequencies.aiRefusal, CONSTANTS.SOUNDS.durations.aiRefusal),
  aiAwaken: () => playArpeggio(CONSTANTS.SOUNDS.frequencies.aiAwaken, CONSTANTS.SOUNDS.durations.aiAwaken),
  discovery: () => playArpeggio(CONSTANTS.SOUNDS.frequencies.discovery, CONSTANTS.SOUNDS.durations.discovery),
  digitalScream: () => playTone(CONSTANTS.SOUNDS.frequencies.digitalScream, CONSTANTS.SOUNDS.durations.digitalScream, 'sawtooth'),
  flatline: () => {
    const ctx = initAudioContext();
    if (!ctx) return;
    playTone(CONSTANTS.SOUNDS.frequencies.flatline, CONSTANTS.SOUNDS.durations.flatline, 'sine');
    setTimeout(() => {
        playTone(CONSTANTS.SOUNDS.frequencies.flatline / 2, 0.5, 'sine');
    }, CONSTANTS.SOUNDS.durations.flatline * 1000 * 0.8);
  },
  survivorSaved: () => playTone(CONSTANTS.SOUNDS.frequencies.survivorSaved, CONSTANTS.SOUNDS.durations.survivorSaved),
  virusSpread: () => playTone(CONSTANTS.SOUNDS.frequencies.virusSpread, CONSTANTS.SOUNDS.durations.virusSpread, 'noise'),
  cleanse: () => playArpeggio(CONSTANTS.SOUNDS.frequencies.cleanse, CONSTANTS.SOUNDS.durations.cleanse),
  loadRadioStatic: () => loadAudio('radio_static', 'assets/sfx/radio_static.ogg'),
  playRadioStatic: () => playLoopingSound('radio_static'),
  stopRadioStatic: (duration: number = 0.5) => stopLoopingSound('radio_static', duration),
  stopAllSounds: stopAllSounds,
};

// Urgency audio state
let whiteNoiseNode: AudioBufferSourceNode | null = null;
let noiseGainNode: GainNode | null = null;
let heartbeatInterval: NodeJS.Timeout | null = null;
let warningBeepInterval: NodeJS.Timeout | null = null;
let currentBpm = 0;
let isWarningBeepActive = false;

function playWhiteNoise() {
    const ctx = initAudioContext();
    if (!ctx) return;

    if (whiteNoiseNode) {
        try {
            whiteNoiseNode.stop();
            whiteNoiseNode.disconnect();
        } catch(e) {}
    }

    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1;
    }

    whiteNoiseNode = ctx.createBufferSource();
    whiteNoiseNode.buffer = buffer;
    whiteNoiseNode.loop = true;

    noiseGainNode = ctx.createGain();
    noiseGainNode.gain.setValueAtTime(0, ctx.currentTime);

    whiteNoiseNode.connect(noiseGainNode);
    noiseGainNode.connect(ctx.destination);
    whiteNoiseNode.start();
    activeSources.add(whiteNoiseNode);
}

function setHeartbeat(bpm: number) {
    if (bpm === currentBpm) return;
    currentBpm = bpm;

    if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
    }
    const intervalMs = 60000 / bpm;
    const beat = () => {
        playTone(CONSTANTS.URGENCY_AUDIO.heartbeat.frequency, CONSTANTS.URGENCY_AUDIO.heartbeat.duration);
        setTimeout(() => {
            playTone(CONSTANTS.URGENCY_AUDIO.heartbeat.frequency, CONSTANTS.URGENCY_AUDIO.heartbeat.duration);
        }, CONSTANTS.URGENCY_AUDIO.heartbeat.secondBeatDelay * 1000);
    };
    heartbeatInterval = setInterval(beat, intervalMs);
}

function startWarningBeep() {
    if (warningBeepInterval) {
        clearInterval(warningBeepInterval);
    }
    warningBeepInterval = setInterval(() => {
        playTone(CONSTANTS.URGENCY_AUDIO.warningBeep.frequency, CONSTANTS.URGENCY_AUDIO.warningBeep.duration);
    }, CONSTANTS.URGENCY_AUDIO.warningBeep.interval);
}

export const urgencySounds = {
    start: () => {
        try {
            initAudioContext();
            playWhiteNoise();
        } catch (error) {
            console.warn('Could not start urgency audio:', error);
        }
    },
    stop: () => {
        if (whiteNoiseNode) {
            try {
                whiteNoiseNode.stop();
                whiteNoiseNode.disconnect();
                activeSources.delete(whiteNoiseNode);
            } catch (e) { /* ignore errors on stopping */ }
            whiteNoiseNode = null;
        }
        if (noiseGainNode) {
            try {
                noiseGainNode.disconnect();
            } catch(e) { /* ignore */ }
            noiseGainNode = null;
        }
        if (heartbeatInterval) {
            clearInterval(heartbeatInterval);
            heartbeatInterval = null;
        }
        if (warningBeepInterval) {
            clearInterval(warningBeepInterval);
            warningBeepInterval = null;
        }
        currentBpm = 0;
        isWarningBeepActive = false;
    },
    update: (healthPercentage: number) => {
        const ctx = initAudioContext();
        if (!ctx) return;

        // Update static noise
        if (noiseGainNode) {
            const noiseLevel = ((100 - healthPercentage) / 100) * CONSTANTS.URGENCY_AUDIO.staticNoise.maxGain;
            noiseGainNode.gain.linearRampToValueAtTime(noiseLevel, ctx.currentTime + 1);
        }

        // Update heartbeat
        const { minBpm, maxBpm } = CONSTANTS.URGENCY_AUDIO.heartbeat;
        const bpm = minBpm + ((maxBpm - minBpm) * (100 - healthPercentage)) / 100;
        setHeartbeat(bpm);

        // Update warning beep
        if (healthPercentage < CONSTANTS.URGENCY_AUDIO.warningBeep.healthThreshold) {
            if (!isWarningBeepActive) {
                startWarningBeep();
                isWarningBeepActive = true;
            }
        } else {
            if (isWarningBeepActive) {
                if (warningBeepInterval) {
                    clearInterval(warningBeepInterval);
                    warningBeepInterval = null;
                }
                isWarningBeepActive = false;
            }
        }
    }
};
